import { ChangeEvent, ReactNode, useEffect, useState } from 'react';

import { CheckMarkIcon } from '@braskai/icons';
import { Button, ModalWrapper, noop, useDeviceType } from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Box, Drawer, Modal, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { enqueueSnackbar } from 'notistack';

import { rememberPendingSubscriptionCheckout } from '@/entities/balance';
import { BOOK_SALES_DEMO_SOURCES, useOpenBookSalesDemo } from '@/entities/demoBooking';
import { trackBusinessPricingClick, usePricingExperimentExposure, useProducts } from '@/entities/pricing';

import { API } from '@/shared/api';
import { MONTHS_IN_YEAR, ONE_UNIT_CENTS_COUNT } from '@/shared/constants/common';
import { PAYMENT_SUBSCRIPTION_CANCELED_PARAM, PAYMENT_SUCCESSFUL_PARAM } from '@/shared/constants/routes/searchParams';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { useInterfaceLanguage } from '@/shared/i18n';
import { TrialPaywallTranslationKey } from '@/shared/i18n/translations/trialPaywall';
import { isBusiness250Plan } from '@/shared/lib/billing/isBusiness250Plan';
import { formatCentsToDollars } from '@/shared/lib/format';
import { getEffectivePriceUnitAmount } from '@/shared/lib/getEffectivePriceUnitAmount';
import { MixpanelEvents } from '@/shared/thirdParty';
import { PaymentInterval, Plan } from '@/shared/types/Billing';
import { ModalHeaderDefault } from '@/shared/ui/modal';
import { Switch } from '@/shared/ui/Switch';

import { getTrialPaywallBenefitMinutesKey, getTrialPaywallMinutesKey } from '../lib/getTrialPaywallTranslationKey';
import {
  DEFAULT_ALLOWED_PLAN_NAMES,
  ENTERPRISE_PLAN_NAME,
  getPaywallContent,
  PaywallBenefit,
} from '../lib/paywallContent';
import { getTrialPlans, TrialPlan } from '../model/PLANS';
import { useTrialPaywallModalStore } from '../model/useTrialPaywallModalStore';
import { PaywallPlanCard } from './PaywallPlanCard';
import { PlanOption } from './PlanOption';

export const PlanOptionsSection = ({
  selectedPlanName,
  visiblePlans,
  handleSelectPlan,
}: {
  selectedPlanName: null | TrialPlan['name'];
  visiblePlans?: TrialPlan[];
  handleSelectPlan: (planName: TrialPlan['name']) => void;
}) => {
  const { t } = useInterfaceLanguage();
  const plans = visiblePlans ?? getTrialPlans(t);

  return (
    <>
      {plans.map((plan: TrialPlan) => (
        <PlanOption
          key={plan.name}
          name={plan.name}
          chipLabel={plan.chipLabel}
          isSelected={plan.name === selectedPlanName}
          minutes={plan.minutes}
          price={plan.price}
          onSelectPlan={handleSelectPlan}
        />
      ))}
    </>
  );
};

const planNameMapping: Record<string, string> = {
  Business: 'Business 500',
  Creator: 'Creator',
  'Creator Pro': 'Creator Pro 100',
};

const getIncludesHeaderKey = (isEnterpriseOnly: boolean, isYearly: boolean): TrialPaywallTranslationKey => {
  if (isEnterpriseOnly) return 'trialPaywall.includes.enterprise';
  if (isYearly) return 'trialPaywall.includes.annual';

  return 'trialPaywall.includes.all';
};

const PlanIncludesSection = ({ isEnterpriseOnly, isYearly }: { isEnterpriseOnly: boolean; isYearly: boolean }) => {
  const { t } = useInterfaceLanguage();
  const { allPlansInclude } = getPaywallContent(t);

  return (
    <Stack gap={12}>
      <Typography color="grey.900" variant="title-4-sb">
        {t(getIncludesHeaderKey(isEnterpriseOnly, isYearly))}
      </Typography>
      {allPlansInclude.map((item) => (
        <Stack key={item} alignItems="center" direction="row" gap={10}>
          <Stack sx={{ color: 'grey.900' }}>
            <CheckMarkIcon height={20} width={20} />
          </Stack>
          <Typography color="grey.900" variant="body-2-md">
            {item}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const BenefitRow = ({ benefit }: { benefit: PaywallBenefit }) => (
  <Stack alignItems="flex-start" direction="row" gap={10}>
    <Stack sx={{ color: 'grey.900' }}>{benefit.icon}</Stack>
    <Stack gap={4}>
      <Typography color="grey.900" variant="body-2-md">
        {benefit.title}
      </Typography>
      <Typography color="grey.500" variant="body-2">
        {benefit.description}
      </Typography>
    </Stack>
  </Stack>
);

const PlanBenefitsSection = ({
  isYearly,
  monthlyMinutes,
  selectedPlanName,
}: {
  isYearly: boolean;
  monthlyMinutes: number;
  selectedPlanName: string;
}) => {
  const { locale, t } = useInterfaceLanguage();
  const { enterpriseBenefits, planBenefits } = getPaywallContent(t);
  const isEnterprise = selectedPlanName === ENTERPRISE_PLAN_NAME;
  const selectedPlanBenefits = planBenefits[selectedPlanName];

  const getBenefits = (): PaywallBenefit[] => {
    if (isEnterprise) return enterpriseBenefits;
    if (!selectedPlanBenefits) return [];
    if (!monthlyMinutes) return selectedPlanBenefits.features;

    const minutes = isYearly ? monthlyMinutes * MONTHS_IN_YEAR : monthlyMinutes;
    const formattedMinutes = new Intl.NumberFormat(locale).format(minutes);
    const minutesTitle = t(getTrialPaywallBenefitMinutesKey(locale, minutes, isYearly), {
      count: formattedMinutes,
    });

    return [
      {
        title: minutesTitle,
        description: selectedPlanBenefits.minutesDescription,
        icon: <CheckMarkIcon height={20} width={20} />,
      },
      ...selectedPlanBenefits.features,
    ];
  };

  return (
    <Stack gap={16}>
      <Typography color="grey.900" variant="title-4-sb">
        {t('trialPaywall.benefits.heading', { plan: selectedPlanName })}
      </Typography>
      {getBenefits().map((benefit) => (
        <BenefitRow key={benefit.title} benefit={benefit} />
      ))}
    </Stack>
  );
};

const TrialPaywallComponent = () => {
  const { isMobile } = useDeviceType();
  const { locale, t } = useInterfaceLanguage();
  const { chipLabels } = getPaywallContent(t);
  const { availableProducts: products, isSuccess } = useProducts(true);
  const preSelectedPlanName = useTrialPaywallModalStore((state) => state.preSelectedPlanName);
  const allowedPlanNames = useTrialPaywallModalStore((state) => state.allowedPlanNames);
  const title = useTrialPaywallModalStore((state) => state.title);
  const onClose = useTrialPaywallModalStore((state) => state.close);
  const openBookSalesDemo = useOpenBookSalesDemo();

  // null/undefined = default self-serve plans; an EMPTY array = Enterprise-only paywall
  const selectablePlanNames = allowedPlanNames ?? DEFAULT_ALLOWED_PLAN_NAMES;
  const isEnterpriseOnly = selectablePlanNames.length === 0;

  usePricingExperimentExposure(products, isSuccess && !isEnterpriseOnly);

  const initiallySelected = (() => {
    if (preSelectedPlanName && selectablePlanNames.includes(preSelectedPlanName)) {
      return preSelectedPlanName;
    }

    if (selectablePlanNames.includes('Creator Pro')) {
      return 'Creator Pro';
    }

    return selectablePlanNames[0] ?? ENTERPRISE_PLAN_NAME;
  })();

  const [selectedPlanName, setSelectedPlanName] = useState<string>(initiallySelected);
  const [paymentPeriod, setPaymentPeriod] = useState<PaymentInterval>(PaymentInterval.Year);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const isYearly = paymentPeriod === PaymentInterval.Year;
  const isEnterpriseSelected = selectedPlanName === ENTERPRISE_PLAN_NAME;

  // Re-sync the selection if the store is re-opened with different allowed plans
  // while the component stays mounted (open-while-open).
  useEffect(() => {
    if (selectedPlanName !== ENTERPRISE_PLAN_NAME && !selectablePlanNames.includes(selectedPlanName)) {
      setSelectedPlanName(initiallySelected);
    }
  }, [initiallySelected, selectablePlanNames, selectedPlanName]);

  // Отправляем событие открытия пейволла один раз
  useExecuteOneTime(() => {
    MixpanelEvents.paywallOpened();
    void API.sendActivitySignal('paywall_opened');
  });

  const getApiPlan = (planName: string): Plan | undefined => {
    const businessOffer = planName === 'Business' ? products?.plan?.find((plan) => isBusiness250Plan(plan)) : undefined;
    return businessOffer ?? products?.plan?.find((plan) => plan.name === planNameMapping[planName]);
  };

  const getActivePrice = (plan: Plan | undefined, interval: PaymentInterval) =>
    plan?.prices.find((price) => price.recurring?.interval === interval && price.status === 'active');

  const getMonthlyMinutes = (planName: string) => Number(getApiPlan(planName)?.metadata?.bundle_value) || 0;

  const handleSelectPlan = (planName: string) => {
    setSelectedPlanName(planName);
  };

  const handlePeriodToggle = (event: ChangeEvent<HTMLInputElement>) => {
    setPaymentPeriod(event.target.checked ? PaymentInterval.Year : PaymentInterval.Month);
  };

  const handleContinueWithPlan = async () => {
    if (isCheckoutLoading) return;
    if (isEnterpriseSelected) {
      MixpanelEvents.enterpriseButtonClick();

      openBookSalesDemo(BOOK_SALES_DEMO_SOURCES.trialPaywallEnterprise, 'Book Demo');
      return;
    }

    MixpanelEvents.continueWithPlanPaywallClick();

    const selectedApiPlan = getApiPlan(selectedPlanName);
    const price = getActivePrice(selectedApiPlan, paymentPeriod);

    if (!selectedApiPlan || !price) {
      enqueueSnackbar(t('trialPaywall.error.generic'), { variant: 'error' });
      return;
    }

    setIsCheckoutLoading(true);
    trackBusinessPricingClick(selectedApiPlan, price.id, products?.pricing_experiment, 'trialModal');

    const currentUrl = window.location.href;
    const successUrl = new URL(currentUrl);
    const cancelUrl = new URL(currentUrl);

    successUrl.searchParams.append(PAYMENT_SUCCESSFUL_PARAM, '');
    cancelUrl.searchParams.append(PAYMENT_SUBSCRIPTION_CANCELED_PARAM, '');

    const MAX_RETRIES = 3;
    let lastError: unknown;
    let checkoutUrl: null | string = null;

    // eslint-disable-next-line no-await-in-loop
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await API.getBuySubscriptionCheckoutUrl({
          id: price.id,
          success_url: successUrl.toString(),
          cancel_url: cancelUrl.toString(),
        });

        if (!response.checkout_url) throw new Error('Missing checkout_url in response');

        checkoutUrl = response.checkout_url;
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
        const status = (error as { status?: number })?.status;
        const isRetryable = !status || status >= 500;

        if (!isRetryable || attempt === MAX_RETRIES) break;

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 1000, null));
      }
    }

    if (lastError || !checkoutUrl) {
      Sentry.captureException(lastError ?? new Error('Missing checkout_url in response'), {
        tags: {
          checkout_failure: 'server',
        },
      });
      enqueueSnackbar(t('trialPaywall.error.generic'), { variant: 'error' });
      setIsCheckoutLoading(false);
      return;
    }

    await rememberPendingSubscriptionCheckout({
      interval: paymentPeriod,
      productId: selectedApiPlan.id,
    }).catch(noop);

    window.location.href = checkoutUrl;

    onClose?.();
  };

  const getCardDetailsRight = (planName: string): ReactNode => {
    const apiPlan = getApiPlan(planName);
    const monthlyMinutes = getMonthlyMinutes(planName);

    if (isYearly) {
      const yearlyPrice = getActivePrice(apiPlan, PaymentInterval.Year);
      if (!yearlyPrice) return null;

      const totalUnitAmount = getEffectivePriceUnitAmount(apiPlan?.category ?? '', yearlyPrice);

      return (
        <Typography color="grey.500" variant="body-2">
          <Typography color="grey.900" component="span" variant="body-2-md">
            ${formatCentsToDollars(totalUnitAmount)}
          </Typography>{' '}
          {t('trialPaywall.card.total')}
        </Typography>
      );
    }

    const monthlyPrice = getActivePrice(apiPlan, PaymentInterval.Month);
    if (!monthlyPrice || !monthlyMinutes) return null;

    const perMinute = Number((monthlyPrice.unit_amount / ONE_UNIT_CENTS_COUNT / monthlyMinutes).toFixed(2));

    return (
      <Typography color="grey.500" variant="body-2">
        <Typography color="grey.900" component="span" variant="body-2-md">
          ${perMinute}
        </Typography>{' '}
        {t('trialPaywall.card.perMinute')}
      </Typography>
    );
  };

  const getCardPriceLabel = (planName: string): ReactNode => {
    const apiPlan = getApiPlan(planName);
    const price = getActivePrice(apiPlan, paymentPeriod);
    if (!price) return '';

    const effectiveUnitAmount = getEffectivePriceUnitAmount(apiPlan?.category ?? '', price);
    const perMonth = isYearly
      ? Math.ceil(effectiveUnitAmount / ONE_UNIT_CENTS_COUNT / MONTHS_IN_YEAR)
      : Math.ceil(effectiveUnitAmount / ONE_UNIT_CENTS_COUNT);

    const monthlyPrice = getActivePrice(apiPlan, PaymentInterval.Month);
    const monthlyPerMonth = monthlyPrice ? Math.ceil(monthlyPrice.unit_amount / ONE_UNIT_CENTS_COUNT) : undefined;

    if (monthlyPerMonth !== undefined && monthlyPerMonth > perMonth) {
      return (
        <>
          <Typography color="grey.300" component="span" sx={{ textDecoration: 'line-through' }} variant="title-4-md">
            {`$${monthlyPerMonth}`}
          </Typography>
          {` $${perMonth}${t('trialPaywall.card.perMonth')}`}
        </>
      );
    }

    return `$${perMonth}${t('trialPaywall.card.perMonth')}`;
  };

  const getCardMinutesLabel = (planName: string) => {
    const monthlyMinutes = getMonthlyMinutes(planName);
    if (!monthlyMinutes) return '';
    const minutes = isYearly ? monthlyMinutes * MONTHS_IN_YEAR : monthlyMinutes;

    const formattedMinutes = new Intl.NumberFormat(locale).format(minutes);

    return isYearly && planName === 'Business'
      ? t('pricing.minutes.included', { count: formattedMinutes })
      : t(getTrialPaywallMinutesKey(locale, minutes), { count: formattedMinutes });
  };

  const isCtaDisabled =
    isCheckoutLoading || (!isEnterpriseSelected && !getActivePrice(getApiPlan(selectedPlanName), paymentPeriod));

  const maxYearlySavingsPercent = Math.max(
    0,
    ...selectablePlanNames.map((planName) => {
      const apiPlan = getApiPlan(planName);
      const monthlyPrice = getActivePrice(apiPlan, PaymentInterval.Month);
      const yearlyPrice = getActivePrice(apiPlan, PaymentInterval.Year);
      if (!monthlyPrice || !yearlyPrice) return 0;

      const monthlyTotal = monthlyPrice.unit_amount * MONTHS_IN_YEAR;
      const yearlyTotal = getEffectivePriceUnitAmount(apiPlan?.category ?? '', yearlyPrice);
      if (monthlyTotal <= 0 || yearlyTotal >= monthlyTotal) return 0;

      return Math.round(((monthlyTotal - yearlyTotal) / monthlyTotal) * 100);
    }),
  );

  const periodToggle = isEnterpriseOnly ? null : (
    <Stack alignItems="center" direction="row" gap={8} sx={{ visibility: isEnterpriseSelected ? 'hidden' : 'visible' }}>
      <Typography color="grey.900" variant="body-2-md">
        {t('trialPaywall.period.payAnnual')}
        {maxYearlySavingsPercent > 0 && (
          <>
            {', '}
            <Typography color="primary.500" component="span" variant="body-2-md">
              {t('trialPaywall.period.savings', { percent: maxYearlySavingsPercent })}
            </Typography>
          </>
        )}
      </Typography>
      <Switch checked={isYearly} data-testid="trial-paywall-period-switch" onChange={handlePeriodToggle} />
    </Stack>
  );

  const planCards = (
    <Stack gap={8}>
      {selectablePlanNames.map((planName) => (
        <PaywallPlanCard
          key={planName}
          name={planName}
          chipLabel={chipLabels[planName]}
          detailsLeft={getCardMinutesLabel(planName)}
          detailsRight={getCardDetailsRight(planName)}
          isSelected={planName === selectedPlanName}
          priceLabel={getCardPriceLabel(planName)}
          onSelectPlan={handleSelectPlan}
        />
      ))}
      <PaywallPlanCard
        name={ENTERPRISE_PLAN_NAME}
        detailsLeft={t('trialPaywall.card.customMinutes')}
        isSelected={isEnterpriseSelected}
        priceLabel={t('trialPaywall.card.customPricing')}
        isCustomPricing
        onSelectPlan={handleSelectPlan}
      />
    </Stack>
  );

  const includesAndBenefits = (
    <>
      <PlanIncludesSection isEnterpriseOnly={isEnterpriseOnly} isYearly={isYearly} />
      <PlanBenefitsSection
        isYearly={isYearly}
        monthlyMinutes={getMonthlyMinutes(selectedPlanName)}
        selectedPlanName={selectedPlanName}
      />
    </>
  );

  const getCtaLabel = () => {
    if (isCheckoutLoading) return t('trialPaywall.cta.processing');
    if (isEnterpriseSelected) return t('trialPaywall.cta.bookDemo');

    return t('trialPaywall.cta.continueWithPlan', { plan: selectedPlanName });
  };

  if (isMobile) {
    return (
      <Drawer
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '90vh',
          },
        }}
        anchor="bottom"
        open
        onClose={onClose}
      >
        <Stack direction="column" sx={{ display: 'flex', height: '100%' }}>
          <ModalHeaderDefault title={title || t('trialPaywall.defaultTitle')} onClose={onClose} />

          {/* Scrollable content area */}
          <Stack
            sx={{
              flex: 1,
              overflowY: 'auto',
              paddingBottom: 2,
            }}
          >
            <Stack gap={16} padding={16}>
              {periodToggle}
              {planCards}
            </Stack>
            <Stack
              sx={{
                backgroundColor: 'grey.50',
              }}
              gap={24}
              padding={32}
              paddingBottom={16}
            >
              {includesAndBenefits}
            </Stack>
          </Stack>

          {/* Fixed button at bottom */}
          <Stack
            sx={{
              backgroundColor: 'white',
              borderTop: '1px solid',
              borderTopColor: 'grey.200',
              bottom: 0,
              position: 'sticky',
              zIndex: 1,
            }}
            padding={16}
          >
            <Button
              data-testid="trial-paywall-continue-with-plan"
              disabled={isCtaDisabled}
              fullWidth
              onClick={handleContinueWithPlan}
            >
              {getCtaLabel()}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    );
  }

  return (
    <Modal
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
      }}
      open
      onClose={onClose}
    >
      <Stack
        direction="row"
        sx={{ backgroundColor: 'common.white', borderRadius: 16, overflow: 'hidden', position: 'relative' }}
        width={800}
      >
        <Stack gap={24} justifyContent="space-between" padding={40} width={400}>
          <Stack gap={24}>
            <Stack gap={8}>
              <Typography color="grey.900" variant="title-3-md">
                {title || t('trialPaywall.defaultTitle')}
              </Typography>
              {periodToggle}
            </Stack>
            {planCards}
          </Stack>
          <Button
            data-testid="trial-paywall-continue-with-plan-2"
            disabled={isCtaDisabled}
            fullWidth
            onClick={handleContinueWithPlan}
          >
            {getCtaLabel()}
          </Button>
        </Stack>
        <Stack gap={24} sx={{ backgroundColor: 'grey.50', padding: '44px 20px 40px 40px' }} width={400}>
          {includesAndBenefits}
        </Stack>
        <Box sx={{ cursor: 'pointer', position: 'absolute', right: 20, top: 28 }}>
          <Close
            data-testid="trial-paywall-close"
            height={24}
            sx={{ color: 'grey.300' }}
            width={24}
            onClick={onClose}
          />
        </Box>
      </Stack>
    </Modal>
  );
};

/**
 * Обертка модалки TrialPaywall.
 * Рендерит содержимое только когда модалка открыта в сторе.
 */
export const TrialPaywall = () => (
  <ModalWrapper hook={useTrialPaywallModalStore}>
    <TrialPaywallComponent />
  </ModalWrapper>
);
